import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck, Circle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const SEVERITY_COLOR = {
  info: "text-neutral-500 bg-neutral-100",
  success: "text-teal bg-teal/10",
  warning: "text-pink bg-pink/10",
  danger: "text-red-500 bg-red-500/10",
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); return `${d}d ago`;
};

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const nav = useNavigate();

  const loadCount = useCallback(async () => {
    if (localStorage.getItem("alfred_notify") === "off") { setUnread(0); return; }
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.count || 0);
    } catch {}
  }, []);

  const loadItems = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch {}
  }, []);

  useEffect(() => {
    loadCount();
    // Poll every 60s
    const id = setInterval(loadCount, 60000);
    return () => clearInterval(id);
  }, [loadCount]);

  useEffect(() => {
    if (open) loadItems();
  }, [open, loadItems]);

  const markRead = async (n) => {
    if (n.read_at) return;
    if (!n.ephemeral) {
      try { await api.patch(`/notifications/${n.notif_id}/read`); } catch {}
    }
    setItems(prev => prev.map(x => x.notif_id === n.notif_id ? { ...x, read_at: new Date().toISOString() } : x));
    setUnread(u => Math.max(0, u - 1));
  };

  const markAll = async () => {
    try { await api.patch("/notifications/read-all"); } catch {}
    setItems(prev => prev.map(x => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
    setUnread(0);
  };

  const openItem = (n) => {
    markRead(n);
    setOpen(false);
    if (n.cta_link) {
      // Preserve hash (e.g. #committee for PACKET_COMMENT deep-links)
      const [pathPart, hashPart] = n.cta_link.split("#");
      const sep = pathPart.includes("?") ? "&" : "?";
      const query = `${pathPart}${sep}notif=${encodeURIComponent(n.notif_id)}&notif_title=${encodeURIComponent(n.title)}`;
      nav(hashPart ? `${query}#${hashPart}` : query);
    }
  };

  const recent = items.slice(0, 6);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="text-white/80 hover:text-white relative" data-testid="notif-bell" aria-label={`Notifications, ${unread} unread`}>
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span
              data-testid="notif-badge"
              className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-pink text-white text-[9px] font-bold grid place-items-center leading-none">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end" data-testid="notif-popover">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="text-sm font-semibold">Notifications</div>
          <button
            onClick={markAll}
            disabled={unread === 0}
            className="text-xs text-pink hover:underline disabled:text-neutral-300 disabled:no-underline flex items-center gap-1"
            data-testid="notif-mark-all-btn">
            <CheckCheck className="w-3 h-3" /> Mark all read
          </button>
        </div>
        <div className="max-h-96 overflow-y-auto" data-testid="notif-list">
          {recent.length === 0 && (
            <div className="p-8 text-center text-sm text-neutral-500" data-testid="notif-empty">
              <Bell className="w-6 h-6 text-neutral-300 mx-auto mb-2" />
              You're all caught up.
            </div>
          )}
          {recent.map((n) => {
            const sevClass = SEVERITY_COLOR[n.severity || "info"];
            const isUnread = !n.read_at;
            return (
              <button
                key={n.notif_id}
                onClick={() => openItem(n)}
                className={`w-full text-left px-4 py-3 border-b hover:bg-neutral-50 transition-colors flex gap-3 ${isUnread ? "bg-pink/[0.02]" : ""}`}
                data-testid={`notif-item-${n.notif_id}`}>
                <div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${sevClass}`}>
                  <Bell className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className={`text-sm ${isUnread ? "font-semibold text-neutral-900" : "text-neutral-700"} truncate`}>{n.title}</div>
                    {isUnread && <Circle className="w-2 h-2 fill-pink text-pink shrink-0 mt-1.5" />}
                  </div>
                  <div className="text-xs text-neutral-500 line-clamp-2 mt-0.5">{n.body}</div>
                  <div className="text-[10px] text-neutral-400 mt-1">{timeAgo(n.created_at)}</div>
                </div>
              </button>
            );
          })}
        </div>
        <div className="border-t px-4 py-2 flex justify-center">
          <Link
            to="/app/notifications"
            onClick={() => setOpen(false)}
            className="text-xs text-pink font-semibold hover:underline"
            data-testid="notif-view-all">
            View all notifications
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
